import React, { useEffect, useState } from 'react'
import { Input, Label } from '../../../styles/newStyles/Register.styled'
import styled from 'styled-components'
import { IoEye, IoEyeOff } from 'react-icons/io5'
import { Text, useMainContext } from '../../../modules/Dashboard'
import LockIcon from '../../../components/icons/LockIcon'
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon'

const initialRules = {
    '8-chars': false,
    'lowercase': false,
    'uppercase': false,
    'number': false,
    'special-chars': false
}

const SetPasswordForm = ({ password, onPasswordChange, setValidationStatus }) => {
    const { main: { lang } } = useMainContext()
    const [confirmPassword, setConfirmPassword] = useState('')
    const [errors, setErrors] = useState({ password: '', confirmPassword: '' })
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [rules, setRules] = useState(initialRules)

    const togglePassword = () => setShowPassword(prev => !prev)
    const toggleConfirm = () => setShowConfirm(prev => !prev)

    const validateForm = (pass, confirm) => {
        const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

        let newErrors = { password: '', confirmPassword: '' }
        let isValid = true

        if (!pass) {
            newErrors.password = lang === "fa" ? "رمز عبور الزامی است." : 'Password is required.'
            isValid = false
        } else if (!passwordRegex.test(pass)) {
            newErrors.password = lang === "fa" ? "رمز عبور باید شامل موارد ذکر شده باشد." : 'Password must include one special character, capital-case, number, and be at least 8 characters.'
            isValid = false
        }

        if (!confirm) {
            newErrors.confirmPassword = lang === "fa" ? "تایید رمز عبور الزامی است." : 'Password confirmation is required.'
            isValid = false
        } else if (pass !== confirm) {
            newErrors.confirmPassword = lang === "fa" ? "رمز عبور با تاییدیه تطابق ندارد." : 'Password does not match!'
            isValid = false
        }

        setErrors(newErrors)
        setValidationStatus(isValid ? 'valid' : 'error')
    }

    const checkPasswordRules = (value) => {
        return {
            '8-chars': value.length >= 8,
            'lowercase': /[a-z]/.test(value),
            'uppercase': /[A-Z]/.test(value),
            'number': /\d/.test(value),
            'special-chars': /[@$!%*?&]/.test(value),
        }
    }

    const handlePasswordChange = (value) => {
        const onlyEnglishRegex = /^[\x00-\x7F]*$/
        if (!onlyEnglishRegex.test(value)) return

        onPasswordChange(value)

        // ✅ Update rules dynamically
        setRules(checkPasswordRules(value))

        // Continue validation
        validateForm(value, confirmPassword)
    }

    const handleConfirmChange = (value) => {
        const onlyEnglishRegex = /^[\x00-\x7F]*$/
        if (!onlyEnglishRegex.test(value)) return

        setConfirmPassword(value)
        validateForm(password, value)
    }

    useEffect(() => setErrors({ password: '', confirmPassword: '' }), [lang])

    return (
        <div className='flex flex-col gap-4 mt-4'>
            <div>
                <Label>
                    <LockIcon size={16} color='#8481BD' />
                    <Text tid="new-password" className='text-inherit' />
                </Label>
                <div className='relative mt-2'>
                    <Input
                        name='password'
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => handlePasswordChange(e.target.value)}
                        placeholder={lang === 'fa' ? "رمز عبور" : "Password"}
                    />
                    <button
                        type="button"
                        onClick={togglePassword}
                        className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2 text-xs"
                    >
                        {showPassword ? <IoEye className='text-textDark dark:text-textLight' size={22} /> : <IoEyeOff className='text-textDark dark:text-textLight' size={22} />}
                    </button>
                </div>
                {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            </div>

            <div>
                <Label>
                    <LockIcon size={16} color='#8481BD' />
                    <Text tid="confirm-new-password" className='text-inherit' />
                </Label>
                <div className='relative mt-2'>
                    <Input
                        name='password-confirmation'
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={e => handleConfirmChange(e.target.value)}
                        onBlur={e => handleConfirmChange(e.target.value)}
                        placeholder={lang === "fa" ? "تایید رمز عبور" : "Password confirmation"}
                    />
                    <button
                        type="button"
                        onClick={toggleConfirm}
                        className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2 text-xs"
                    >
                        {showConfirm ? <IoEye size={22} className='text-textDark dark:text-textLight' /> : <IoEyeOff size={22} className='text-textDark dark:text-textLight' />}
                    </button>
                </div>
                {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
            </div>

            {/* ✅ Password Rules Section */}
            <div className='grid grid-cols-2 gap-2'>
                <div className='col-span-2'>
                    <Text tid={"password-must-contains"} className='text-secondaryColor' />
                </div>
                {Object.entries(rules).map(([key, value]) => (
                    <div className='flex items-center gap-1' key={key}>
                        <CheckCircleIcon size={12} color={value ? '#00C795' : "#869BDD"} />
                        <Text tid={key} className={`${value ? "text-[#00C795]" : "text-secondaryColor"} text-sm`} />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default SetPasswordForm

export const ErrorMessage = styled.div`
	color: #e74c3c;
	font-size: 0.875rem;
	margin-top: 0.25rem;
`
